import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    listActionPlanCandidates,
    acceptActionPlanCandidate,
    rejectActionPlanCandidate,
    createActionPlanCandidate,
    listContextDefinitions,
} from './actionCandidatesApi'

// Action-plan candidate hooks + context-definition listing. Moved out of the
// retired plan system's hook file (usePlans.js, deleted with the plan system —
// 2026-07-27 consolidation).

export const useActionPlanCandidates = (
    workspaceID,
    { sourceGroupId, scopeEntityId, includeRejected } = {}
) => {
    return useQuery({
        queryKey: [
            'actionPlanCandidates',
            workspaceID,
            sourceGroupId,
            scopeEntityId,
            includeRejected,
        ],
        queryFn: () =>
            listActionPlanCandidates(workspaceID, {
                sourceGroupId,
                scopeEntityId,
                includeRejected,
            }),
        enabled: Boolean(workspaceID),
    })
}

export const useAcceptActionPlanCandidate = (workspaceID) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (candidateFingerprint) =>
            acceptActionPlanCandidate(workspaceID, candidateFingerprint),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['actionPlanCandidates', workspaceID] })
        },
    })
}

export const useRejectActionPlanCandidate = (workspaceID) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ candidateFingerprint, reason }) =>
            rejectActionPlanCandidate(workspaceID, { candidateFingerprint, reason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['actionPlanCandidates', workspaceID] })
        },
    })
}

export const useCreateActionPlanCandidate = (workspaceID) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (candidate) => createActionPlanCandidate(workspaceID, candidate),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['actionPlanCandidates', workspaceID] })
        },
    })
}

export const useContextDefinitions = (workspaceID) => {
    return useQuery({
        queryKey: ['contextDefinitions', workspaceID],
        queryFn: () => listContextDefinitions(workspaceID),
        enabled: Boolean(workspaceID),
    })
}
